import { Capacitor } from "@capacitor/core";
import {
  type SendMessageRefusal,
  SendMessageRefusalSchema,
} from "@vidimo/shared/contracts";
import { interfaceTranslator } from "@/i18n/translate";
import { subscriptionRequiredMessage } from "@/lib/api/api-error";
import { readRefusal } from "@/lib/api-error-message";
import { apiJson, sendApiRequest } from "@/lib/api-request";
import { preparingMessage } from "@/lib/operational-state";

interface TwilioTokenResponse {
  identity: string;
  token: string;
}

export function fetchTwilioVoiceToken(): Promise<TwilioTokenResponse> {
  return apiJson<TwilioTokenResponse>("/twilio/voice/token", {
    label: "Fetch Twilio token",
    query: {
      platform: Capacitor.getPlatform() === "android" ? "android" : "ios",
    },
  });
}

/**
 * Blind-transfer the live call to another staff member of `propertyId`, the
 * property the call rang for, or of the current property when null. The
 * server resolves the guest's parent leg from `callSid` (the device's own
 * leg), drops the caller, and rings `targetMemberId`. On success the
 * caller's leg ends naturally (a `callDisconnected` event).
 */
export function transferCall(input: {
  callSid: string;
  propertyId: string | null;
  targetMemberId: string;
}): Promise<{ success: boolean }> {
  return apiJson<{ success: boolean }>("/twilio/voice/transfer", {
    body: input,
    label: "Transfer call",
    method: "POST",
  });
}

function sendRefusalMessage(refusal: SendMessageRefusal | null): string {
  const t = interfaceTranslator("communications.dialerDrawer");
  if (!refusal) {
    return t("errorPermissionRequest");
  }
  if (refusal.code === "property_preparing") {
    return preparingMessage();
  }
  if (refusal.code === "subscription_required") {
    return subscriptionRequiredMessage();
  }
  return t(`refusal.${refusal.code}`);
}

interface SendWhatsAppTemplateArgs {
  templateName: "call_request";
  to: string;
}

/**
 * Sends a WhatsApp template message. Used to request call permission before
 * placing a WhatsApp voice call — WhatsApp policy requires prior opt-in for
 * user-initiated business calls. Throws a message safe to show.
 */
export async function sendWhatsAppTemplate({
  to,
  templateName,
}: SendWhatsAppTemplateArgs): Promise<{ messageSid: string }> {
  const res = await sendApiRequest("/twilio/message/send", {
    body: { channel: "whatsapp", templateName, to },
    method: "POST",
  });

  if (!res.ok) {
    throw new Error(
      sendRefusalMessage(await readRefusal(res, SendMessageRefusalSchema))
    );
  }
  const data = await res.json().catch((cause: unknown) => {
    throw new Error(sendRefusalMessage(null), { cause });
  });
  return { messageSid: data.messageSid };
}
